import FormLabel from '@c/forms/controls/FormLabel';
import FormSelect, { SelectOption } from '@c/forms/controls/FormSelect';
import PageSection from '@c/listing/shared/PageSection';
import Checkbox from '@ui/Checkbox';
import Select from '@ui/Select';
import { getNumber, getSizeOptions } from '@util/createListingHelpers';
import { ProductDocument } from 'models/product';
import { useState, useEffect } from 'react';
import { useCreateListing } from '../_context';
import FormCounter from '@c/forms/controls/FormCounter';
import FormCurrencyInputFancy from '@c/forms/controls/FormCurrencyInputFancy';
import FormInput from '@c/forms/controls/FormInput';
import { Controller } from 'react-hook-form';
import { Divider } from '@ui/Divider';
import { formatCurrency, isMobile } from '@util/index';
import { SaleFeature } from '@c/icons/sale';
import Toggle from '@ui/Toggle';

export const GearFitment = () => {
  const { form, product, variant } = useCreateListing();
  const [variationSalePrice, setVariationSalePrice] = useState<number | null>(
    null
  );
  const [sizeType, setSizeType] = useState<'boots' | 'pants' | 'letter' | ''>(
    ''
  );

  useEffect(() => {
    if (sizeType) return;
    if (
      product.category1 === 'Boots' ||
      product.category1 === 'Footwear' ||
      product.category1 === 'Shoes'
    ) {
      setSizeType('boots');
    } else if (product.category2 === 'Pants') {
      setSizeType('pants');
    } else {
      setSizeType('letter');
    }
  }, [product.category1, product.category2, sizeType]);

  const [hasLetterSize, setHasLetterSize] = useState(
    product?.variations?.some((v) => v.size !== 'N/A' && !v.is_number)
  );
  const [hasNumberSize, setHasNumberSize] = useState(
    product?.variations?.some((v) => v.size !== 'N/A' && v.is_number)
  );
  const [jerseySizes, setJerseySizes] = useState<string[]>(
    (
      product?.variations
        ?.filter((v) => v.size !== 'N/A' && !v.is_number)
        .map((v) => v.size) ?? []
    ).map((s) => `${s}`.toUpperCase())
  );
  const [pantSizes, setPantSizes] = useState<string[]>(
    (
      product?.variations
        ?.filter((v) => v.size !== 'N/A' && v.is_number)
        .map((v) => v.size) ?? []
    ).map((s) => `${s}`.toUpperCase())
  );

  useEffect(() => {
    if (
      product.has_variations &&
      product.variations &&
      product.variations.length !==
        (jerseySizes?.length ?? 0) + (pantSizes?.length ?? 0)
    ) {
      const variations: ProductDocument['variations'] = [];

      jerseySizes?.forEach((size) => {
        const existingVariation = product.variations?.find(
          (v) => v.size === size
        );
        if (existingVariation) {
          variations.push(existingVariation);
        } else {
          variations.push({ size, qty: 0, price: 0, is_number: false });
        }
      });

      pantSizes?.forEach((size) => {
        const existingVariation = product.variations?.find(
          (v) => v.size === +size
        );
        if (existingVariation) {
          variations.push(existingVariation);
        } else {
          variations.push({ size: +size, qty: 0, price: 0, is_number: true });
        }
      });
      form.setValue('variations', variations);
    }
  }, [
    jerseySizes,
    pantSizes,
    product.has_variations,
    form,
    variant,
    product.variations,
  ]);

  function setProductPriceFromVariations(
    newVariations: ProductDocument['variations']
  ) {
    // find the cheapest variation and set it as the price / og_price
    let price = product.price;
    if (product.has_variations && newVariations?.length) {
      if (product.category2 === 'Gear Combos') {
        const letterSizeVaritaions = newVariations.filter(
          (v) => !v.is_number && v.qty > 0
        );
        const numberSizeVaritaions = newVariations.filter(
          (v) => v.is_number && v.qty > 0
        );
        // price is the minimum combo price of the two variations
        const minLetterPrice = Math.min(
          ...(letterSizeVaritaions.map((v) => v.price) ?? [0])
        );
        const minNumberPrice = Math.min(
          ...(numberSizeVaritaions.map((v) => v.price) ?? [0])
        );
        price = minLetterPrice + minNumberPrice;
      } else {
        const inStockVariations = newVariations?.filter((v) => v.qty > 0);
        if (inStockVariations.length === 0) {
          price = 0;
        } else {
          price = Math.max(...(inStockVariations.map((v) => v.price) ?? [0]));
        }
      }
      form.setValue('price', price);
      if (!product.on_sale) {
        form.setValue('og_price', price);
      }
    }
    if (price > 0) {
      form.clearErrors('price');
    }
  }

  return (
    <PageSection
      title="Gear Fitment"
      switchTitle="This Product Has Variations"
      switchOnChange={() =>
        form.setValue('has_variations', !product.has_variations)
      }
      switchEnabled={product.has_variations}
      showSwitch={product.condition === 'New' && !isMobile()}
      headerClassName="!sm:w-[110rem]"
      error={form.formState.errors.variations?.message}
    >
      <div className="mt-8 flex w-full justify-between gap-x-16">
        <div className="flex w-full flex-col gap-y-8">
          <FormLabel value="Gender" required>
            <FormSelect
              allowEmpty={false}
              name="gender"
              control={form.control}
              options={[
                { label: 'Men', value: 'M', id: 'M' },
                { label: 'Women', value: 'W', id: 'W' },
                { label: 'Unisex', value: 'U', id: 'U' },
                { label: 'Youth', value: 'Y', id: 'Y' },
              ]}
            />
          </FormLabel>

          {!product.has_variations ? (
            <>
              {product.category2 === 'Gear Combos' ? (
                <div className="flex flex-col gap-y-4">
                  <div className="flex items-center gap-x-2">
                    <FormLabel
                      value="Jersey Size"
                      errorMessage={form.formState.errors.size?.letter?.message}
                    >
                      <FormSelect
                        allowEmpty={false}
                        name="size.letter"
                        control={form.control}
                        options={getSizeOptions(
                          'letter',
                          product.gender ?? 'U'
                        )}
                      />
                    </FormLabel>

                    <FormLabel
                      value="Pant Size"
                      errorMessage={form.formState.errors.size?.number?.message}
                    >
                      <FormSelect
                        allowEmpty={false}
                        name="size.number"
                        control={form.control}
                        options={getSizeOptions('pants', product.gender ?? 'U')}
                      />
                    </FormLabel>
                  </div>
                </div>
              ) : product.category2 !== 'Goggles' ? (
                <div className="flex flex-col gap-y-6">
                  <div className="flex flex-col gap-[1.6rem] sm:flex-row">
                    <FormLabel value={'Size Type'}>
                      <Select
                        allowEmpty={false}
                        options={[
                          {
                            label: 'Letter',
                            value: 'letter',
                            id: 'letter',
                          },
                          {
                            label: 'Boots',
                            value: 'boots',
                            id: 'boots',
                          },
                          {
                            label: 'Pants',
                            value: 'pants',
                            id: 'pants',
                          },
                        ]}
                        value={sizeType}
                        onChange={(e: any) => {
                          setSizeType(e.value);
                        }}
                      ></Select>
                    </FormLabel>
                    <FormLabel
                      value={`Select Size`}
                      errorMessage={
                        form.formState.errors.size?.letter?.message ||
                        form.formState.errors.size?.number?.message
                      }
                    >
                      <div
                        className={
                          sizeType === 'boots' || sizeType === 'pants'
                            ? 'block'
                            : 'hidden'
                        }
                      >
                        <FormSelect
                          allowEmpty={false}
                          name={'size.number'}
                          control={form.control}
                          options={getSizeOptions(
                            sizeType === 'boots' ? 'boots' : 'pants',
                            product.gender ?? 'U'
                          )}
                        />
                      </div>

                      <div
                        className={
                          sizeType === 'boots' || sizeType === 'pants'
                            ? 'hidden'
                            : 'block'
                        }
                      >
                        <FormSelect
                          allowEmpty={false}
                          name={'size.letter'}
                          control={form.control}
                          options={getSizeOptions(
                            'letter',
                            product.gender ?? 'U'
                          )}
                        />
                      </div>
                    </FormLabel>
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <div className="mt-8 flex flex-col">
              <strong className="text-[1.8rem]">Choose Options</strong>
              <span>*You will set price and quantiy in the next step</span>

              <div className="mt-6 flex flex-col gap-y-6">
                <Checkbox
                  label="Letter Size (s)"
                  selected={hasLetterSize}
                  onChange={() => setHasLetterSize(!hasLetterSize)}
                />

                <Checkbox
                  label="Number Size (s)"
                  selected={hasNumberSize}
                  onChange={() => setHasNumberSize(!hasNumberSize)}
                />
              </div>

              <div className="mt-16 flex w-full flex-col gap-16 sm:w-[70rem] sm:flex-row">
                {hasLetterSize && (
                  <FormLabel value="Select Letter Sizes">
                    <Select
                      allowEmpty={false}
                      options={getSizeOptions('letter', product.gender ?? 'U')}
                      multiple
                      value={jerseySizes}
                      onChange={(e: any) => {
                        setJerseySizes(
                          (e as SelectOption[]).map((size) => size.value)
                        );
                      }}
                    />
                  </FormLabel>
                )}

                {hasNumberSize && (
                  <FormLabel value="Select Number Sizes">
                    <Select
                      allowEmpty={false}
                      options={getSizeOptions(
                        ['Boots', 'Footwear', 'Shoes'].includes(
                          product.category1 ?? ''
                        )
                          ? 'boots'
                          : 'pants',
                        product.gender ?? 'U'
                      )}
                      multiple
                      value={pantSizes}
                      onChange={(e: any) =>
                        setPantSizes(
                          (e as SelectOption[]).map((size) => `${size.value}`)
                        )
                      }
                    />
                  </FormLabel>
                )}
              </div>

              <div className="mt-16 flex flex-col gap-4">
                <div className="flex items-center gap-[1.6rem] font-semibold">
                  <span className="w-[6rem]">Size</span>
                  <span className="w-[21rem]">Quantity</span>
                  <span className="w-[22rem]">SKU/MPN</span>
                  <span className="w-[15rem]">Price</span>
                </div>

                <Divider />
                {product.variations?.map((variation, index) => {
                  return (
                    <div
                      className="flex items-center gap-[1.6rem]"
                      key={variation.size + (variation.color ?? '')}
                    >
                      <span className="w-[6rem] font-semibold">
                        {variation.size}
                      </span>

                      <div>
                        <FormCounter
                          value={variation.qty}
                          onChange={(e) => {
                            if (e < 0) return;
                            const currentVariations = product.variations ?? [];
                            if (!currentVariations) return;
                            const index = currentVariations.findIndex(
                              (v) => v.size === variation.size
                            );
                            if (index !== -1) {
                              currentVariations[index].qty = e;
                            }
                            form.setValue('variations', [...currentVariations]);
                            if (currentVariations.some((v) => v.qty > 0)) {
                              form.clearErrors('variations');
                            }
                          }}
                          error={false}
                        />
                      </div>

                      <div>
                        <FormInput
                          value={variation.sku}
                          onChange={(e) => {
                            form.setValue(
                              `variations.${index}.sku`,
                              e.target.value
                            );
                          }}
                          defaultValue={variation.sku}
                          placeholder="SKU/MPN"
                        />
                      </div>

                      <Controller
                        name={`variations.${index}.price`}
                        control={form.control}
                        render={({ field }) => (
                          <FormCurrencyInputFancy
                            placeholder="0.00"
                            value={field.value}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value);
                              if (!isNaN(value)) {
                                field.onChange(value.toFixed(2));
                              }
                            }}
                          />
                        )}
                      />
                    </div>
                  );
                })}
              </div>
              {variant === 'edit-listing' && (
                <>
                  <div className="my-[1.6rem] flex w-min gap-[1.6rem] whitespace-nowrap">
                    <div className="flex w-full justify-between gap-4">
                      <div className="flex items-center gap-4 font-semibold">
                        <SaleFeature
                          fill={'#C41719'}
                          width={'40'}
                          height={'40'}
                        />
                        Sale Price
                      </div>
                      <Toggle
                        enabled={product.on_sale}
                        onChange={() =>
                          form.setValue('on_sale', !product.on_sale)
                        }
                      />
                    </div>
                  </div>
                  {product.on_sale && (
                    <div className="flex flex-col gap-4">
                      <FormLabel value="Sale Price">
                        <FormCurrencyInputFancy
                          placeholder={'0.00'}
                          defaultValue={variationSalePrice?.toString() ?? ''}
                          onChange={(e) => {
                            setVariationSalePrice(getNumber(e.target.value));
                            const newVariations = product.variations?.map(
                              (v) => ({
                                ...v,
                                price: getNumber(e.target.value),
                              })
                            );
                            form.setValue('variations', newVariations);
                            setProductPriceFromVariations(newVariations);
                          }}
                        />
                      </FormLabel>
                      <span>
                        Original Price: {formatCurrency(product.og_price)}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="hidden flex-col gap-y-6 sm:flex">
          <h3 className="text-[1.8rem] font-semibold text-black">
            Provide complete info about the product.
          </h3>

          <p className="text-[1.5rem] text-zinc-600">
            Enhance your product description to build trust with potential
            buyers.
          </p>
        </div>
      </div>
    </PageSection>
  );
};
